import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Shield, Clock, Calendar, TrendingUp, Loader, CheckCircle } from 'lucide-react';
import { schedulingService } from '../../services/schedulingService';

const SchedulingRiskDashboard = ({ caseId }) => {
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [recoveryOptions, setRecoveryOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRecovery, setShowRecovery] = useState(false);

  const fetchRiskAssessment = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await schedulingService.getRiskAssessment(caseId);
      setRiskAssessment(response.data);

      // If risk is high, fetch recovery options
      if (response.data.overall_risk_score >= 50) {
        const delayDays = response.data.early_warning_days <= 0 ? 7 : 3;
        const recoveryResponse = await schedulingService.getRecoveryOptions(caseId, delayDays);
        setRecoveryOptions(recoveryResponse.data || []);
      }
    } catch (err) {
      console.error('Error fetching risk assessment:', err);
      setError('Failed to load risk assessment');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchRiskAssessment();
  }, [fetchRiskAssessment]);

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'critical':
        return { bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-100' };
      case 'high':
        return { bg: 'bg-orange-500', text: 'text-orange-700', light: 'bg-orange-100' };
      case 'medium':
        return { bg: 'bg-yellow-500', text: 'text-yellow-700', light: 'bg-yellow-100' };
      case 'low':
        return { bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-100' };
      default:
        return { bg: 'bg-gray-500', text: 'text-gray-700', light: 'bg-gray-100' };
    }
  };

  const getEffortBadge = (effort) => {
    switch (effort) {
      case 'low':
        return 'bg-green-100 text-green-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'high':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-center py-8">
          <Loader className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Analyzing schedule risks...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border p-6 text-center">
        <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
        <p className="text-gray-600">{error}</p>
        <button onClick={fetchRiskAssessment} className="mt-2 text-blue-600 hover:text-blue-800">
          Retry
        </button>
      </div>
    );
  }

  if (!riskAssessment) {
    return null;
  }

  const colors = getRiskLevelColor(riskAssessment.overall_risk_level);

  return (
    <div className="bg-white rounded-lg border">
      {/* Header with Risk Score */}
      <div className={`p-4 border-b ${colors.light}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Shield className={`h-6 w-6 mr-2 ${colors.text}`} />
            <div>
              <h3 className="text-lg font-semibold">Scheduling Risk Assessment</h3>
              <p className={`text-sm capitalize ${colors.text}`}>
                {riskAssessment.overall_risk_level} Risk Level
              </p>
            </div>
          </div>

          {/* Risk Gauge */}
          <div className="relative w-24 h-12">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${colors.bg} transition-all duration-500`}
                  style={{ width: `${riskAssessment.overall_risk_score}%` }}
                />
              </div>
            </div>
            <div className="absolute right-0 -top-1 text-lg font-bold">
              {riskAssessment.overall_risk_score.toFixed(0)}
            </div>
          </div>
        </div>

        {/* Early Warning */}
        {riskAssessment.early_warning_days <= 7 && (
          <div className="mt-3 flex items-center text-sm">
            <Clock className="h-4 w-4 mr-1" />
            {riskAssessment.early_warning_days <= 0 ? (
              <span className="text-red-700 font-medium">Immediate action required</span>
            ) : (
              <span>
                <span className="font-medium">{riskAssessment.early_warning_days}</span> days of buffer remaining
              </span>
            )}
          </div>
        )}
      </div>

      {/* Deadline Risks */}
      {riskAssessment.deadline_risks && riskAssessment.deadline_risks.length > 0 && (
        <div className="p-4 border-b">
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <Calendar className="h-4 w-4 mr-1" />
            Deadline Risk Analysis
          </h4>
          <div className="space-y-3">
            {riskAssessment.deadline_risks.map((deadline, index) => {
              const deadlineColors = getRiskLevelColor(deadline.risk_level);
              return (
                <div key={index} className={`p-3 rounded border ${deadlineColors.light}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900">{deadline.title}</div>
                      <div className="text-sm text-gray-600">
                        {new Date(deadline.deadline_date).toLocaleDateString()}
                        {deadline.is_immovable && (
                          <span className="ml-2 text-xs bg-red-100 text-red-700 px-1 rounded">
                            Immovable
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-medium capitalize ${deadlineColors.text}`}>
                        {deadline.risk_level}
                      </div>
                      <div className="text-xs text-gray-500">
                        {deadline.days_until} days
                      </div>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Completion</span>
                      <span>{deadline.completion_percentage}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          deadline.completion_percentage >= 80 ? 'bg-green-500' :
                          deadline.completion_percentage >= 50 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${deadline.completion_percentage}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{deadline.completed_tasks}/{deadline.total_tasks} tasks</span>
                      <span>Float: {deadline.min_float_days?.toFixed(1) || 0}d</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Risk Factors */}
      {riskAssessment.risk_factors && riskAssessment.risk_factors.length > 0 && (
        <div className="p-4 border-b">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Risk Factors
          </h4>
          <div className="space-y-2">
            {riskAssessment.risk_factors.map((factor, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-2 rounded ${
                  factor.severity === 'high' ? 'bg-red-50' : 'bg-yellow-50'
                }`}
              >
                <div className="flex items-center">
                  <AlertTriangle className={`h-4 w-4 mr-2 ${
                    factor.severity === 'high' ? 'text-red-500' : 'text-yellow-500'
                  }`} />
                  <span className="text-sm">{factor.description}</span>
                </div>
                <span className="text-xs text-gray-500">
                  +{factor.score_impact} risk
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {riskAssessment.recommendations && riskAssessment.recommendations.length > 0 && (
        <div className="p-4 border-b">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Recommendations
          </h4>
          <div className="space-y-2">
            {riskAssessment.recommendations.map((rec, index) => (
              <div
                key={index}
                className={`p-2 rounded text-sm ${
                  rec.includes('CRITICAL') ? 'bg-red-50 text-red-700' :
                  rec.includes('Monitor') ? 'bg-blue-50 text-blue-700' :
                  rec.includes('on track') ? 'bg-green-50 text-green-700' :
                  'bg-gray-50 text-gray-700'
                }`}
              >
                {rec}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recovery Options */}
      {recoveryOptions.length > 0 && (
        <div className="p-4">
          <button
            onClick={() => setShowRecovery(!showRecovery)}
            className="w-full flex items-center justify-between text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            <span className="flex items-center">
              <TrendingUp className="h-4 w-4 mr-1" />
              Recovery Options ({recoveryOptions.length})
            </span>
            <span>{showRecovery ? '−' : '+'}</span>
          </button>

          {showRecovery && (
            <div className="mt-3 space-y-3">
              {recoveryOptions.map((option, index) => (
                <div key={index} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900 capitalize">
                        {option.option_type.replace(/_/g, ' ')}
                      </div>
                      <div className="text-sm text-gray-600">{option.description}</div>
                    </div>
                    <span className={`px-2 py-0.5 text-xs rounded ${getEffortBadge(option.effort_required)}`}>
                      {option.effort_required} effort
                    </span>
                  </div>

                  <div className="mt-2 flex items-center space-x-4 text-sm">
                    <span className="text-green-600">
                      Saves {option.time_saved_days.toFixed(1)} days
                    </span>
                    <span className="text-gray-500">
                      {(option.confidence * 100).toFixed(0)}% confidence
                    </span>
                  </div>

                  {option.risks && option.risks.length > 0 && (
                    <div className="mt-2 text-xs text-amber-600">
                      Risks: {option.risks.join(', ')}
                    </div>
                  )}

                  {option.implementation_steps && option.implementation_steps.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-500 mb-1">Implementation:</div>
                      <ol className="text-xs text-gray-600 list-decimal list-inside">
                        {option.implementation_steps.slice(0, 3).map((step, idx) => (
                          <li key={idx}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Low Risk State */}
      {riskAssessment.overall_risk_level === 'low' && (
        <div className="p-6 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
          <p className="text-green-700 font-medium">Schedule is on track</p>
          <p className="text-sm text-gray-500 mt-1">
            Continue monitoring progress to maintain low risk
          </p>
        </div>
      )}
    </div>
  );
};

export default SchedulingRiskDashboard;
